import React, {useMemo} from 'react';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {O1Page} from '@/pages/components/audit-program/o-section/constants';
import {O1_PAGE} from '@/pages/components/audit-program/o-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface O1ShareCapitalViewProps {
    header: AuditProgramSectionVO;
    page: O1Page;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {...cellBorder, fontWeight: 600, fontSize: '0.75rem', py: 0.75, px: 0.5};
const bodySx = {...cellBorder, py: 0.45, px: 0.4, verticalAlign: 'top' as const, overflow: 'hidden'};
const sectionSx = {...cellBorder, py: 0.6, px: 0.5, fontWeight: 700, bgcolor: 'action.hover'};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

/** O1 — Share capital lead schedule (issued / shareholders / directors). */
export default function O1ShareCapitalView({
    header,
    page,
    refCode = 'O1',
    values,
    onChange,
}: O1ShareCapitalViewProps) {
    const meta = {
        title: page.title || O1_PAGE.title,
        cyLabel: page.cyLabel || O1_PAGE.cyLabel,
        lyLabel: page.lyLabel || O1_PAGE.lyLabel,
        sections: page.sections?.length ? page.sections : O1_PAGE.sections,
        remarksTitle: page.remarksTitle || O1_PAGE.remarksTitle,
        remarks: page.remarks?.length ? page.remarks : O1_PAGE.remarks,
    };

    const sectionRows = useMemo(
        () => meta.sections.map((sec) => ({
            ...sec,
            rowIds: Array.from({length: Math.max(1, sec.blankRows)}, (_, i) => `${sec.id}${i + 1}`),
        })),
        [meta.sections],
    );

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 1.5, mt: 0.5}}
            >
                {meta.title}
            </Typography>

            <TableContainer sx={{mb: 2, maxWidth: 780}}>
                <Table size="small" sx={{tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col/>
                        <col style={{width: 56}}/>
                        <col style={{width: 140}}/>
                        <col style={{width: 48}}/>
                        <col style={{width: 140}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}>Description</TableCell>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx} align="right">{meta.cyLabel}</TableCell>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx} align="right">{meta.lyLabel}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sectionRows.map((sec) => (
                            <React.Fragment key={sec.id}>
                                <TableRow>
                                    <TableCell colSpan={5} sx={sectionSx}>{sec.label}</TableCell>
                                </TableRow>
                                {sec.rowIds.map((id) => (
                                    <TableRow key={id}>
                                        <TableCell sx={bodySx}>
                                            <InputField
                                                type="text"
                                                fullWidth
                                                compact
                                                sx={fieldSx}
                                                value={values[`${id}.name`] ?? ''}
                                                onChange={(e) => onChange(`${id}.name`, e.target.value)}
                                            />
                                        </TableCell>
                                        <TableCell sx={bodySx}>
                                            <InputField
                                                type="text"
                                                fullWidth
                                                compact
                                                align="center"
                                                sx={fieldSx}
                                                value={values[`${id}.tickCy`] ?? ''}
                                                onChange={(e) => onChange(`${id}.tickCy`, e.target.value)}
                                            />
                                        </TableCell>
                                        <TableCell sx={bodySx}>
                                            <InputField
                                                type="text"
                                                fullWidth
                                                compact
                                                align="right"
                                                sx={fieldSx}
                                                value={values[`${id}.cy`] ?? ''}
                                                onChange={(e) => onChange(`${id}.cy`, e.target.value)}
                                            />
                                        </TableCell>
                                        <TableCell sx={bodySx}>
                                            <InputField
                                                type="text"
                                                fullWidth
                                                compact
                                                align="center"
                                                sx={fieldSx}
                                                value={values[`${id}.tickLy`] ?? ''}
                                                onChange={(e) => onChange(`${id}.tickLy`, e.target.value)}
                                            />
                                        </TableCell>
                                        <TableCell sx={bodySx}>
                                            <InputField
                                                type="text"
                                                fullWidth
                                                compact
                                                align="right"
                                                sx={fieldSx}
                                                value={values[`${id}.ly`] ?? ''}
                                                onChange={(e) => onChange(`${id}.ly`, e.target.value)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.5}}>
                {meta.remarksTitle}
            </Typography>
            <Stack spacing={0.25}>
                {meta.remarks.map((r, idx) => (
                    <Typography key={`${r.tick}-${idx}`} variant="body2" sx={{pl: 0.5, lineHeight: 1.4}}>
                        <Box component="span" sx={{display: 'inline-block', minWidth: 44, fontWeight: 600}}>
                            {r.tick}
                        </Box>
                        {r.text}
                    </Typography>
                ))}
            </Stack>
        </Box>
    );
}
